import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [healthMetrics, setHealthMetrics] = useState({
    activeServers: 24,
    deliveryRate: 98.7,
    criticalAlerts: 2
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const getStatusColor = (alerts) => {
    if (alerts === 0) return 'text-success';
    if (alerts <= 2) return 'text-warning';
    return 'text-error';
  };

  const notifications = [
    { id: 1, type: 'critical', message: 'Server srv-canary-01 quarantined', time: '5 min ago' },
    { id: 2, type: 'warning', message: 'Blacklist detection on srv-prod-03', time: '15 min ago' },
    { id: 3, type: 'info', message: 'Campaign "Spring Launch" completed', time: '1 hour ago' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-100 bg-card border-b border-border transition-smooth ${
        isScrolled ? 'shadow-md' : ''
      }`}
    >
      <div className="flex items-center justify-between h-20 px-6">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-3 hover-lift">
            <div className="header-logo">
              <Icon name="Zap" size={24} className="header-logo-icon" />
            </div>
            <span className="text-xl font-heading font-semibold text-foreground">
              Chimera Dashboard
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-2">
            <Link
              to="/system-overview"
              className={`px-4 py-2 rounded-lg font-medium transition-smooth hover-lift ${
                isActivePath('/system-overview')
                  ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              System Overview
            </Link>
            <Link
              to="/server-detail"
              className={`px-4 py-2 rounded-lg font-medium transition-smooth hover-lift ${
                isActivePath('/server-detail')
                  ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              Server Detail
            </Link>
            <Link
              to="/campaign-manager"
              className={`px-4 py-2 rounded-lg font-medium transition-smooth hover-lift ${
                isActivePath('/campaign-manager')
                  ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              Campaign Manager
            </Link>
            <Link
              to="/war-room-dashboard"
              className={`px-4 py-2 rounded-lg font-medium transition-smooth hover-lift ${
                isActivePath('/war-room-dashboard')
                  ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
              }`}
            >
              War Room
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-6 px-6 py-2 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="Server" size={16} className="text-muted-foreground" />
              <span className="text-sm font-mono font-medium text-foreground">
                {healthMetrics?.activeServers}
              </span>
              <span className="text-xs text-muted-foreground">Active</span>
            </div>
            
            <div className="w-px h-6 bg-border" />
            
            <div className="flex items-center gap-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span className="text-sm font-mono font-medium text-foreground">
                {healthMetrics?.deliveryRate}%
              </span>
              <span className="text-xs text-muted-foreground">Rate</span>
            </div>
            
            <div className="w-px h-6 bg-border" />
            
            <div className="flex items-center gap-2">
              <Icon 
                name="AlertCircle" 
                size={16} 
                className={getStatusColor(healthMetrics?.criticalAlerts)} 
              />
              <span className={`text-sm font-mono font-medium ${getStatusColor(healthMetrics?.criticalAlerts)}`}>
                {healthMetrics?.criticalAlerts}
              </span>
              <span className="text-xs text-muted-foreground">Alerts</span>
            </div>
          </div>

          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              iconName="Search"
              iconSize={20}
              className="hidden lg:flex"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
            />
            {isSearchOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-card rounded-lg border border-border shadow-lg p-4 z-50">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Search" size={16} className="text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search campaigns, servers, contacts..."
                    className="flex-1 bg-transparent text-sm text-foreground outline-none"
                    autoFocus
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  Press Enter to search
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              iconName="Bell"
              iconSize={20}
              className="relative"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            >
              {healthMetrics?.criticalAlerts > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full animate-pulse-subtle" />
              )}
            </Button>
            {isNotificationsOpen && (
              <div className="absolute right-0 mt-2 w-96 bg-card rounded-lg border border-border shadow-lg z-50">
                <div className="p-4 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications?.map((notif) => (
                    <div key={notif?.id} className="p-4 border-b border-border hover:bg-muted transition-colors">
                      <div className="flex items-start gap-3">
                        <Icon 
                          name={notif?.type === 'critical' ? 'AlertCircle' : notif?.type === 'warning' ? 'AlertTriangle' : 'Info'} 
                          size={16} 
                          className={notif?.type === 'critical' ? 'text-error' : notif?.type === 'warning' ? 'text-warning' : 'text-primary'}
                        />
                        <div className="flex-1">
                          <p className="text-sm text-foreground">{notif?.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{notif?.time}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 text-center border-t border-border">
                  <button className="text-xs text-primary hover:text-primary/80">
                    View All Notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              iconName="Settings"
              iconSize={20}
              className="hidden lg:flex"
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            />
            {isSettingsOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-card rounded-lg border border-border shadow-lg z-50">
                <div className="p-2">
                  <Link to="/system-overview" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">Profile Settings</span>
                  </Link>
                  <Link to="/system-overview" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors">
                    <Icon name="Bell" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">Notifications</span>
                  </Link>
                  <Link to="/system-overview" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors">
                    <Icon name="Shield" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">Security</span>
                  </Link>
                  <div className="border-t border-border my-2" />
                  <button className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors w-full text-left">
                    <Icon name="LogOut" size={16} className="text-error" />
                    <span className="text-sm text-error">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pl-6 border-l border-border">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div className="hidden xl:block">
              <div className="text-sm font-medium text-foreground">Admin User</div>
              <div className="text-xs text-muted-foreground">System Administrator</div>
            </div>
          </div>
        </div>
      </div>
      <nav className="lg:hidden flex items-center gap-2 px-6 pb-4 overflow-x-auto scrollbar-thin">
        <Link
          to="/system-overview"
          className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            isActivePath('/system-overview')
              ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
          }`}
        >
          System Overview
        </Link>
        <Link
          to="/server-detail"
          className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            isActivePath('/server-detail')
              ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
          }`}
        >
          Server Detail
        </Link>
        <Link
          to="/campaign-manager"
          className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            isActivePath('/campaign-manager')
              ? 'bg-primary text-primary-foreground' :'text-foreground hover:bg-muted'
          }`}
        >
          Campaign Manager
        </Link>
      </nav>
    </header>
  );
};

export default Header;